// unydo-frontend/src/api/axios.ts
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

const RAW_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "");

if (!RAW_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not set");
}

const API_ORIGIN = RAW_BASE.replace(/\/api\/?$/, "");

const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || "csrf_token";
const CSRF_HEADER_NAME = "x-csrf-token";

const api: AxiosInstance = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: true,
});

// ---------------- Access token (memory) ----------------
let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}

// ---------------- CSRF token (memory) ----------------
// IMPORTANT: Avoid relying on "read cookie right now" during race conditions.
// Keep the last known csrf token in memory and use it consistently.
let csrfTokenMem: string | null = null;

// Single-flight CSRF fetch (prevents multiple /auth/csrf calls racing)
let csrfInFlight: Promise<string | null> | null = null;

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const m = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function isUnsafeMethod(method?: string) {
  const m = (method || "GET").toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

/**
 * Ensures we have a CSRF token available.
 * - single-flight to prevent parallel calls rotating the cookie
 * - stores returned token in memory (best) and falls back to cookie
 */
export async function ensureCsrf(): Promise<string | null> {
  if (csrfTokenMem) return csrfTokenMem;

  if (!csrfInFlight) {
    csrfInFlight = (async () => {
      try {
        const res = await api.get("/auth/csrf", { skipAuthRefresh: true } as any);
        const token = (res.data as any)?.token || getCookie(CSRF_COOKIE_NAME);
        csrfTokenMem = token || null;
        return csrfTokenMem;
      } catch {
        // fallback to cookie if request failed for some reason
        csrfTokenMem = getCookie(CSRF_COOKIE_NAME);
        return csrfTokenMem;
      } finally {
        csrfInFlight = null;
      }
    })();
  }

  return csrfInFlight;
}

// ---------------- Request interceptor ----------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers = config.headers || {};

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Send CSRF header on unsafe requests
  if (isUnsafeMethod(config.method)) {
    // Use memory token first; fallback to cookie
    const csrf = csrfTokenMem || getCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      (config.headers as any)[CSRF_HEADER_NAME] = csrf;
    }
  }

  // Let the browser set FormData boundary
  const isForm = typeof FormData !== "undefined" && config.data instanceof FormData;
  if (isForm) {
    delete (config.headers as any)["Content-Type"];
    delete (config.headers as any)["content-type"];
  }

  return config;
});

// ---------------- Refresh single-flight ----------------
let refreshInFlight: Promise<string> | null = null;

async function doRefreshOnce(): Promise<string> {
  // Ensure csrf exists (and is stable) BEFORE refresh
  const csrf = await ensureCsrf();

  // Try refresh
  try {
    const refreshRes = await api.post(
      "/auth/refresh",
      {},
      { skipAuthRefresh: true } as any
    );

    const newToken = (refreshRes.data as any)?.accessToken;
    if (!newToken) throw new Error("No accessToken returned from refresh");

    setAccessToken(newToken);
    return newToken;
  } catch (e: any) {
    // If CSRF check failed, it’s usually because cookie rotated.
    // Fetch fresh csrf and retry refresh ONE time.
    const status = e?.response?.status;
    if (status === 403) {
      csrfTokenMem = null;          // drop memory token
      await ensureCsrf();           // get the newest one
      const retryRes = await api.post(
        "/auth/refresh",
        {},
        { skipAuthRefresh: true } as any
      );
      const newToken = (retryRes.data as any)?.accessToken;
      if (!newToken) throw new Error("No accessToken returned from refresh (retry)");
      setAccessToken(newToken);
      return newToken;
    }
    throw e;
  }
}


// api/axios.ts or equivalent
export async function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshOnce().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}



// ---------------- Response interceptor ----------------
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<any>) => {
    const status = error.response?.status;
    const original: any = error.config;

    if (!original || original._retry) throw error;

    const isRefreshCall =
      original.url?.includes("/auth/refresh") || (original as any).skipAuthRefresh;

    // Only refresh on 401 from normal endpoints
    if (status === 401 && !isRefreshCall) {
      original._retry = true;

      try {
        const newToken = await refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshErr) {
        setAccessToken(null);
        throw refreshErr;
      }
    }

    throw error;
  }
);

export default api;
